import {
  Avatar,
  colorsTuple,
  defaultVariantColorsResolver,
  Input,
  MantineThemeColorsOverride,
  MantineThemeOverride,
  mergeThemeOverrides,
  parseThemeColor,
  rem,
} from "@mantine/core"
import {
  ButtonVariantColorResolver,
  themeButton,
} from "./components/Button/Button"
import { themeDrawer } from "./components/DrawerKiosk/DrawerKiosk"
import { themeSelect } from "./components/SelectKiosk/SelectKiosk"

import classes from "./theme.module.css"

import "dayjs/locale/fr"
import dayjs from "dayjs"

dayjs.locale("fr")

export const colors = {
  "kiosk-green": colorsTuple("#00A53D"),
  "kiosk-light": colorsTuple("#D1FAE5"),
  green: [
    "#F0FDF4",
    "#DCFCE7",
    "#BBF7D0",
    "#86EFAC",
    "#4ADE80",
    "#22C55E",
    "#16A34A",
    "#15803D",
    "#166534",
    "#14532D",
    "#052E16",
  ],
  emerald: [
    "#ECFDF5",
    "#D1FAE5",
    "#A7F3D0",
    "#6EE7B7",
    "#34D399",
    "#10B981",
    "#059669",
    "#047857",
    "#065F46",
    "#064E3B",
    "#022C22",
  ],
  teal: [
    "#F0FDFA",
    "#CCFBF1",
    "#99F6E4",
    "#5EEAD4",
    "#2DD4BF",
    "#14B8A6",
    "#0D9488",
    "#0F766E",
    "#115E59",
    "#134E4A",
    "#042F2E",
  ],
  gray: [
    "#F8FAFC",
    "#F1F5F9",
    "#E2E8F0",
    "#CBD5E1",
    "#94A3B8",
    "#64748B",
    "#475569",
    "#334155",
    "#1E293B",
    "#0F172A",
    "#020617",
  ],
  blue: [
    "#ECFEFF",
    "#CFFAFE",
    "#A5F3FC",
    "#67E8F9",
    "#22D3EE",
    "#06B6D4",
    "#0891B2",
    "#0E7490",
    "#155E75",
    "#164E63",
    "#083344",
  ],
  orange: [
    "#FFF7ED",
    "#FFEDD5",
    "#FED7AA",
    "#FDBA74",
    "#FB923C",
    "#F97316",
    "#EA580C",
    "#C2410C",
    "#9A3412",
    "#7C2D12",
    "#431407",
  ],
  purple: [
    "#F5F3FF",
    "#EDE9FE",
    "#DDD6FE",
    "#C4B5FD",
    "#A78BFA",
    "#8B5CF6",
    "#7C3AED",
    "#6D28D9",
    "#5B21B6",
    "#4C1D95",
    "#2E1065",
  ],
  yellow: [
    "#FFFBEB",
    "#FEF9C7",
    "#FDE68A",
    "#FCD34D",
    "#FBBF24",
    "#F59E0B",
    "#D97706",
    "#B45309",
    "#92400E",
    "#78350F",
    "#451A03",
  ],
  indigo: [
    "#EEF2FF",
    "#E0E7FF",
    "#C7D2FE",
    "#A5B4FC",
    "#818CF8",
    "#6366F1",
    "#4F46E5",
    "#4338CA",
    "#3730A3",
    "#312E81",
  ],
} as const

export type Color = keyof typeof colors

const themeDefault: MantineThemeOverride = {
  // Colors
  colors: colors as MantineThemeColorsOverride,
  primaryColor: "green",
  black: colors.gray[8],

  fontFamily: "Inter",
  headings: {
    fontFamily: "Inter",
    sizes: {
      h1: {
        fontSize: rem(20),
        fontWeight: "500",
      },
      h2: {
        fontSize: rem(18),
        fontWeight: "600",
      },
      h3: {
        fontSize: rem(16),
        fontWeight: "600",
      },
    },
  },

  fontSizes: {
    xxs: rem(11),
    xxl: rem(24),
  },

  lineHeights: {
    xxs: "1.2",
  },

  spacing: {
    none: rem(0),
    xxs: rem(8),
    xl: rem(24),
    xxl: rem(36),
  },

  radius: { xs: rem(4), sm: rem(6) },

  variantColorResolver: (input) => {
    const parsedColor = parseThemeColor({
      color: input.color || input.theme.primaryColor,
      theme: input.theme,
    })
    const defaultResolvedColors = defaultVariantColorsResolver(input)
    const customButtonColors = ButtonVariantColorResolver(input)
    const currentColor = parsedColor.color as Color

    if (customButtonColors) {
      return customButtonColors
    }
    if (input.variant === "outlight") {
      return {
        background: colors[currentColor][0],
        hover: colors[currentColor][1],
        color: colors.gray[8],
        border: `1px solid ${colors[currentColor][6]}`,
      }
    }

    return defaultResolvedColors
  },

  components: {
    Avatar: Avatar.extend({
      vars: (_, props) => {
        if (props.size === "xxl") {
          return {
            root: {
              "--avatar-size": rem(100),
            },
          }
        }
        if (props.size === "xl") {
          return {
            root: {
              "--avatar-size": rem(40),
            },
          }
        }
        if (props.size === "lg") {
          return {
            root: {
              "--avatar-size": rem(32),
            },
          }
        }
        if (props.size === "md") {
          return {
            root: {
              "--avatar-size": rem(26),
            },
          }
        }
        if (props.size === "sm") {
          return {
            root: {
              "--avatar-size": rem(24),
            },
          }
        }
        return { root: {} }
      },
    }),
    Divider: { defaultProps: { color: "var(--mantine-color-gray-2)" } },
    Paper: {
      defaultProps: { radius: "sm", withBorder: true },
      styles: {
        root: { borderColor: "var(--mantine-color-gray-2)" },
      },
    },
    Badge: {
      defaultProps: { radius: "sm", size: "lg" },
      classNames: { root: classes.badge },
    },
    Text: {
      defaultProps: { size: "sm", colors: colors.gray[8] },
    },
    Input: Input.extend({
      defaultProps: { size: "md" },
      classNames: {
        input: classes.input,
      },
      vars: (_, props) => {
        if (props.size === "sm") {
          return {
            wrapper: {
              "--input-height": rem(38),
            },
          }
        }
        return { wrapper: {} }
      },
    }),
    Select: { classNames: classes.input },
    TextInput: {
      defaultProps: { size: "md" },
    },
    InputWrapper: {
      classNames: { label: classes.input__label },
    },
    NumberInput: {
      defaultProps: {
        hideControls: true,
      },
    },
    DateInput: {
      defaultProps: { locale: "fr", valueFormat: "DD MMM YYYY" },
    },
    DatePickerInput: {
      defaultProps: { locale: "fr", valueFormat: "DD MMM YYYY", size: "md" },
    },
    Textarea: {
      defaultProps: {
        minRows: 2,
        autosize: true,
        resize: "vertical",
      },
      styles: {
        input: { borderColor: colors.gray[2] },
      },
    },

    ActionIcon: {
      defaultProps: { variant: "white", size: 20 },
    },
    ThemeIcon: {
      defaultProps: {
        variant: "white",
        size: 20,
        color: "gray.8",
      },
      styles: {
        root: { background: "transparent", display: "flex" },
      },
    },
    Checkbox: {
      styles: { input: { cursor: "pointer" }, label: { cursor: "pointer" } },
    },
    Table: {
      defaultProps: { borderColor: "gray.2", horizontalSpacing: "md" },
      styles: {
        th: {
          color: "var(--mantine-color-gray-5)",
          fontWeight: 400,
        },
      },
    },
    Modal: {
      styles: {
        title: {
          fontSize: 24,
          fontWeight: 600,
        },
        header: {
          padding: 24,
        },
      },
    },
    Menu: {
      defaultProps: { position: "bottom-end" },
    },
    Pill: {
      styles: {
        root: {
          background: colors.purple[1],
          color: colors.purple[8],
          paddingTop: 1,
          paddingBottom: 1,
          height: 24,
        },
      },
    },
    HoverCard: {
      styles: {
        dropdown: {
          backgroundColor: colors.gray[8],
          color: colors.gray[1],
          paddingX: 16,
          paddingY: 8,
        },
      },
    },
    Dialog: {
      defaultProps: {
        radius: "md",
        shadow: "xl",
        p: 0,
        mih: 0,
        size: "auto",
        withBorder: true,
      },
    },
    List: {
      defaultProps: { listStyleType: "none" },
      styles: {
        itemWrapper: {
          width: "100%",
        },
        itemLabel: {
          width: "100%",
        },
      },
    },
    Title: {
      styles: {
        root: {
          display: "flex",
          alignItems: "center",
        },
      },
    },
  },
}

export const theme: MantineThemeOverride = mergeThemeOverrides(
  themeDefault,
  themeButton,
  themeDrawer,
  themeSelect,
)
